// ResourceLoader (minimal): cache con TTL + dedupe in-flight + invalidate.
// --------------------------------------------------------------
// Concetti FUTURI (non implementati, solo promemoria):
// - SWR (Stale-While-Revalidate): se la cache è scaduta ma hai un valore "stale",
//   puoi restituire subito quello e in background fare una fetch per aggiornare la cache.
//   Dove inserirlo: dentro get(), nel ramo "cache scaduta", prima di avviare la fetch.
//
// - prime (idratazione cache): inserire manualmente un valore in cache (es. da WP inline).
//   Dove inserirlo: metodo `prime(key, value, ttlMs)` che chiama internamente setCache(...).
//
// - mutate (UI ottimistica): aggiornare il valore in cache localmente (es. push nuova review)
//   e poi eventualmente revalidare. Dove inserirlo: metodo `mutate(key, updater)` che legge
//   il valore corrente da cache, applica l'updater e richiama setCache(...).
// --------------------------------------------------------------

export type Fetcher<K, V> = (key: K, ctx?: unknown) => Promise<V>;

type CacheEntry<V> = {
  value: V;
  expiresAt: number;
};

export type LoaderOpts<K, V> = {
  fetcher: Fetcher<K, V>;
  ttlMs?: number;
  serializeKey?: (key: K) => string;
};

export class ResourceLoader<K, V> {
  private fetcher: Fetcher<K, V>;
  private ttlMs: number;
  private inflight = new Map<string, Promise<V>>();         // dedupe richieste concorrenti
  private cache = new Map<string, CacheEntry<V>>();
  private serializeKey: (key: K) => string;

  constructor(opts: LoaderOpts<K, V>) {
    this.fetcher = opts.fetcher;
    this.ttlMs = opts.ttlMs ?? 5 * 60_000;
    this.serializeKey = opts.serializeKey ?? ((k) => JSON.stringify(k));
  }

  private now() { return Date.now(); }
  private toKey(key: K) { return this.serializeKey(key); }

  /**
   * Ritorna il valore per `key`.
   * - se cache "fresca": ritorna subito il valore
   * - se esiste una fetch in-flight per la stessa key: dedupe (restituisce la stessa Promise)
   * - altrimenti avvia la fetch, aggiorna cache e ritorna la Promise
   */

  async get(
    key: K,
    opts?: { force?: boolean; ctx?: unknown; ttlMs?: number }
  ): Promise<V> {
    const sk = this.toKey(key);
    const ttl = opts?.ttlMs ?? this.ttlMs;

    const entry = this.cache.get(sk);
    const fresh = !!entry && entry.expiresAt > this.now();

    if (!opts?.force && fresh) {
      return entry!.value;
    }

    const running = this.inflight.get(sk);
    if (running) return running;

    const p = this.fetcher(key, opts?.ctx)
      .then((val) => {
        this.setCache(sk, val, ttl);
        this.inflight.delete(sk);
        return val;
      })
      .catch((err) => {
        this.inflight.delete(sk);
        throw err;
      });

    this.inflight.set(sk, p);
    return p;
  }

  /** Invalida la cache (per chiave o tutta) */
  clear(key?: K) {
    if (key === undefined) {
      this.cache.clear();
      this.inflight.clear();
      return;
    }
    const sk = this.toKey(key);
    this.cache.delete(sk);
    this.inflight.delete(sk);
  }

  /** (utility) Ritorna il valore in cache se presente e fresco, altrimenti undefined */
  getCached(key: K): V | undefined {
    const sk = this.toKey(key);
    const e = this.cache.get(sk);
    if (e && e.expiresAt > this.now()) return e.value;
    return undefined;
  }

  private setCache(sk: string, value: V, ttlMs: number) {
    this.cache.set(sk, { value, expiresAt: this.now() + ttlMs });
  }
}
